import { useEffect, useRef } from "react"
import { cn } from "@/lib/utils"
import { HighlightText } from "@/components/highlight-text"
import { slideAlternatingBlocks, slideInFrom, sectionHeaderClasses } from "@/lib/section-slide"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

const pillars = [
  {
    title: "Dedicated Teams",
    description: "A team assigned solely to your program, not shared across clients.",
  },
  {
    title: "Secure Infrastructure",
    description: "Engagements run on your IT and your tools — your IP never leaves your environment.",
  },
  {
    title: "Scalable Engagement",
    description: "Ramp the team up or down as your tape-out schedule demands.",
  },
  {
    title: "Predictable Costs",
    description: "Fixed-scope or managed engagements with clear, predictable pricing.",
  },
]

export function ReassureSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!sectionRef.current || !headerRef.current || !gridRef.current) return

    const ctx = gsap.context(() => {
      slideInFrom(headerRef.current, "left")
      slideAlternatingBlocks(gridRef.current, "[data-reassure-card]", "left")

      const icons = gridRef.current?.querySelectorAll("[data-reassure-icon]")
      if (icons?.length) {
        gsap.fromTo(
          icons,
          { scale: 0.6, opacity: 0, rotate: -8 },
          {
            scale: 1,
            opacity: 1,
            rotate: 0,
            duration: 0.7,
            stagger: 0.14,
            ease: "back.out(1.6)",
            scrollTrigger: {
              trigger: gridRef.current,
              start: "top 88%",
              toggleActions: "play none none reverse",
            },
          },
        )
      }

      const dividers = gridRef.current?.querySelectorAll("[data-reassure-divider]")
      if (dividers?.length) {
        gsap.fromTo(
          dividers,
          { scaleY: 0 },
          {
            scaleY: 1,
            duration: 0.9,
            stagger: 0.1,
            ease: "power3.inOut",
            scrollTrigger: {
              trigger: gridRef.current,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          },
        )
      }
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} id="reassure" className="relative border-t border-border/30 py-32 pl-6 md:pl-28 pr-6 md:pr-12">
      <div ref={headerRef} className={cn("mb-16 max-w-3xl", sectionHeaderClasses("left"))}>
        <span className="mb-4 block font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
          Reassure
        </span>
        <h2 className="mt-4 font-[var(--font-bebas)] text-5xl leading-[1.05] tracking-tight uppercase md:text-7xl">
          <HighlightText>How</HighlightText> The Engagement
          <br />
          Actually Runs
        </h2>
        <p className="mt-6 font-mono text-xs leading-relaxed text-muted-foreground md:text-sm">
          A dedicated centre on your tools and your IP — run like an extension of your team.
        </p>
      </div>

      <div ref={gridRef} className="grid grid-cols-1 gap-px border border-border/30 bg-border/30 sm:grid-cols-2 md:grid-cols-4">
        {pillars.map((pillar, index) => (
          <article
            key={pillar.title}
            data-reassure-card
            className="group relative flex flex-col bg-card/90 p-8 md:p-10"
          >
            {index > 0 && (
              <div
                data-reassure-divider
                className="absolute bottom-8 left-0 top-8 hidden w-px origin-top bg-border/60 md:block"
                aria-hidden="true"
              />
            )}

            <div
              data-reassure-icon
              className="mb-8 h-5 w-5 border border-accent transition-colors duration-300 group-hover:bg-accent/15"
              aria-hidden="true"
            />

            <h3 className="font-[var(--font-bebas)] text-2xl tracking-tight uppercase transition-colors duration-300 group-hover:text-accent md:text-3xl">
              {pillar.title}
            </h3>

            <div className="my-4 h-px w-8 bg-accent/50 transition-all duration-500 group-hover:w-full" />

            <p className="font-mono text-xs leading-relaxed text-muted-foreground">{pillar.description}</p>
          </article>
        ))}
      </div>
    </section>
  )
}
